import { useEffect, useState } from "react";
import MobileDestinationChips from "../components/MobileDestinationChips";
import MobileHero from "../components/MobileHero";
import MobileHorizontalSection from "../components/MobileHorizontalSection";
import MobilePropertyCard from "../components/MobilePropertyCard";
import MobileLayout from "../layouts/MobileLayout";
import { normalizePropertyCard } from "../mobileUtils";
import { resolveMediaUrl } from "../../utils/resolveMediaUrl";
import { apiGet } from "../../legacy/services/apiClient";
import { extractElivaasProviderId } from "../../utils/providerRoutes";

const getRailBanner = (rail = {}) => {
  const banners = Array.isArray(rail.topBanners) ? rail.topBanners : [];
  return rail.banner || banners[0] || null;
};

const normalizeAdminBanner = (banner) => {
  if (!banner) return null;
  return {
    id: banner.id || banner._id,
    title: banner.title || "",
    subtitle: banner.subtitle || "",
    imageUrl: banner.imageUrl || banner.image_url || banner.image || "",
    ctaText: banner.ctaText || banner.cta_text || "",
    ctaLink: banner.ctaLink || banner.cta_link || banner.link || "",
    meta: banner.eyebrow ? { kicker: banner.eyebrow } : banner.meta || {},
    kicker: banner.eyebrow || banner.kicker || "",
  };
};

const hasMeaningfulSideBannerContent = (banner) =>
  Boolean(
    String(banner?.kicker || "").trim() ||
      String(banner?.title || "").trim() ||
      String(banner?.subtitle || "").trim() ||
      String(banner?.ctaText || banner?.cta || "").trim() ||
      String(banner?.ctaLink || "").trim() ||
      String(banner?.meta?.kicker || banner?.meta?.eyebrow || "").trim()
  );

const normalizeBannerDecor = (payload = {}) => {
  const sectionPayload =
    payload?.sections && typeof payload.sections === "object"
      ? payload.sections
      : payload;
  return (
  Object.fromEntries(
    Object.entries(sectionPayload || {}).map(([sectionKey, sectionValue]) => {
      const topBanners = (Array.isArray(sectionValue?.top) ? sectionValue.top : sectionValue?.topBanners || [])
        .map(normalizeAdminBanner)
        .filter(Boolean);
      return [
        sectionKey,
        {
          topBanners,
          sideBanner: normalizeAdminBanner(sectionValue?.side || sectionValue?.sideBanner),
        },
      ];
    })
  )
  );
};

const applyBannerDecor = (rail = {}, decor = {}) => {
  const sectionKey = rail.key || rail.sectionKey || rail.id;
  const entry = decor?.[sectionKey];
  if (!entry) return rail;
  const topBanners = entry.topBanners?.length ? entry.topBanners : rail.topBanners;
  const sideBanner = hasMeaningfulSideBannerContent(entry.sideBanner) ? entry.sideBanner : rail.sideBanner;
  return {
    ...rail,
    topBanners,
    sideBanner,
    banner: topBanners?.[0] || sideBanner || rail.banner,
  };
};

const toLocalIsoDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const buildDefaultProviderDateRange = () => {
  const checkInDate = new Date();
  const checkOutDate = new Date(checkInDate);
  checkOutDate.setDate(checkOutDate.getDate() + 1);
  return {
    checkIn: toLocalIsoDate(checkInDate),
    checkOut: toLocalIsoDate(checkOutDate),
  };
};

const positiveNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

const needsProviderPriceRefresh = (item = {}) => {
  if (!item?.is_external && item?.source !== "api") return false;
  const price = positiveNumber(
    item.price ||
      item.display_sell_price ||
      item.final_per_night_display_price ||
      item.sell_price_per_night ||
      item.price_per_night ||
      item.base_price_per_night
  );
  return !price || price === 108000 || price === 120000 || price === 240000;
};

const refreshProviderRailPrices = async (items = []) => {
  const dateRange = buildDefaultProviderDateRange();
  return Promise.all(
    items.map(async (item) => {
      if (!needsProviderPriceRefresh(item)) return item;
      const providerId = extractElivaasProviderId(item) || item.provider_property_id || item.providerPropertyId;
      if (!providerId) return item;
      try {
        const detail = await apiGet(
          `/api/providers/elivaas/properties/${encodeURIComponent(providerId)}?${new URLSearchParams({
            checkIn: dateRange.checkIn,
            checkOut: dateRange.checkOut,
            adults: "2",
            children: "0",
          }).toString()}`
        );
        const source = detail?.data || detail?.item || detail || {};
        const refreshedPrice =
          positiveNumber(source.price_per_night) ||
          positiveNumber(source.pricePerNight) ||
          positiveNumber(source.sell_price_per_night) ||
          positiveNumber(source.display_sell_price) ||
          positiveNumber(source.base_price_per_night) ||
          positiveNumber(source.provider_base_price);
        if (!refreshedPrice) return item;
        return {
          ...item,
          ...source,
          id: item.id,
          source: item.source || "api",
          is_external: item.is_external ?? true,
          provider_property_id: providerId,
          price: refreshedPrice,
          price_per_night: refreshedPrice,
          pricePerNight: refreshedPrice,
          sell_price_per_night: refreshedPrice,
          display_sell_price: refreshedPrice,
        };
      } catch (_) {
        return item;
      }
    })
  );
};

const railItemIdentity = (item = {}) =>
  String(item.provider_property_id || item.providerPropertyId || item.id || item.slug || item.title || item.name || "");

const mergeRailItems = (...groups) => {
  const seen = new Set();
  return groups.flatMap((group) => (Array.isArray(group) ? group : [])).filter((item) => {
    const key = railItemIdentity(item);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const fetchProviderRailBridgeRows = async (sectionKey) => {
  const readRows = async (url) => {
    const response = await fetch(url, { credentials: "include" });
    if (!response.ok) return null;
    const payload = await response.json();
    return Array.isArray(payload?.items) ? payload.items : [];
  };
  try {
    const rows = await readRows(
      `/homepage-provider-rail.php?${new URLSearchParams({ section: sectionKey, limit: "6" }).toString()}`
    );
    if (rows) return rows;
  } catch (_) {
    return null;
  }
  return null;
};

const MobileRailBanner = ({ banner }) => {
  const image = resolveMediaUrl(banner?.imageUrl || banner?.image_url || banner?.image, {
    width: 720,
    height: 250,
    fit: "cover",
    quality: 76,
  });
  if (!banner || !image) return null;

  const content = (
    <>
      <img src={image} alt={banner.title || "Homevilas banner"} loading="lazy" />
      {banner.meta?.kicker || banner.kicker || banner.eyebrow ? (
        <span>{banner.meta?.kicker || banner.kicker || banner.eyebrow}</span>
      ) : null}
      {banner.title ? <strong>{banner.title}</strong> : null}
    </>
  );

  return banner.ctaLink || banner.cta_link || banner.link ? (
    <a className="hv-mobile-rail-banner" href={banner.ctaLink || banner.cta_link || banner.link}>
      {content}
    </a>
  ) : (
    <div className="hv-mobile-rail-banner">{content}</div>
  );
};

export default function MobileHome() {
  const [rails, setRails] = useState([]);
  const visibleRails = rails;

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      apiGet("/api/public/homepage"),
      apiGet("/api/public/banners/home").catch(() => ({})),
    ])
      .then(([payload, bannersPayload]) => {
        const bannerDecor = normalizeBannerDecor(bannersPayload);
        const nextRails = (payload?.rails || [])
          .map((rail) => applyBannerDecor(rail, bannerDecor))
          .filter((rail) => Array.isArray(rail.items) && rail.items.length);
        if (controller.signal.aborted) return;
        setRails(nextRails.map((rail) => ({ ...rail, items: [] })));

        Promise.all(
          nextRails.map(async (rail) => {
            const sectionKey = rail.key || rail.sectionKey || rail.id || "popular";
            const bridgeRows = await fetchProviderRailBridgeRows(sectionKey);
            const sourceRows =
              Array.isArray(bridgeRows) && bridgeRows.length
                ? bridgeRows
                : rail.items || [];
            const items = await refreshProviderRailPrices(mergeRailItems(sourceRows).slice(0, 6));
            return { ...applyBannerDecor(rail, bannerDecor), items };
          })
        )
          .then((refreshedRails) => {
            if (!controller.signal.aborted) setRails(refreshedRails);
          })
          .catch(() => {});
      })
      .catch((error) => { if (error.name !== "AbortError") setRails([]); })
      .finally(() => {});
    return () => controller.abort();
  }, []);

  return (
    <MobileLayout showFooter>
      <MobileHero />
      <section className="hv-section">
        <div className="hv-section-head">
          <h2 className="hv-section-title">Where to next?</h2>
        </div>
        <MobileDestinationChips />
      </section>
      {visibleRails.length ? visibleRails.map((rail) => <MobileHorizontalSection
        key={rail.key || rail.title}
        title={rail.title}
        viewAll={rail.actionLink}
        banner={<MobileRailBanner banner={getRailBanner(rail)} />}
      >
        {rail.items.map((item,index) => { const property=normalizePropertyCard(item,index); return <MobilePropertyCard key={property.id} property={property} horizontal />; })}
      </MobileHorizontalSection>) : null}
    </MobileLayout>
  );
}
